import React, { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import './Main.css';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import { FaEye, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import Preloader from './Preloader';


function MissingReportInteractions() {
  const navigate = useNavigate();
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, pending, reviewed, resolved
  const [selectedInteractions, setSelectedInteractions] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [missingReports, setMissingReports] = useState({});
  const itemsPerPage = 25; // Increased from 10 to show more reports per page
  const [page, setPage] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  useEffect(() => {
    document.title = "Found Reports";
  }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchInteractions();
    }
  }, [currentUser]);

  useEffect(() => {
    // Temporarily disable real-time updates for testing
    /*
    if (currentUser) {
      // Real-time updates for found reports
      const interactionsChannel = supabase
        .channel('public:found_reports_interactions')
        .on('postgres_changes',
          { event: '*', schema: 'public', table: 'found_reports' },
          (payload) => {
            console.log('Found report change received!', payload);
            fetchInteractions();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(interactionsChannel);
      };
    }
    */
  }, [currentUser]);

  async function fetchInteractions() {
    setLoading(true);
    setError('');
    
    try {
      // Fetch found reports from users about missing reports
      const { data: foundReportsData, error: foundReportsError } = await supabase
        .from('found_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (foundReportsError) {
        console.error('Error fetching found reports:', foundReportsError);
        throw foundReportsError;
      }

      const allInteractions = (foundReportsData || []).map(item => ({ 
        ...item, 
        interaction_type: 'found_report',
        user_id: item.finder_user_id // Map finder_user_id to user_id for consistency
      }));
      
      setInteractions(allInteractions);

      // Fetch user profiles for both finders and owners
      const finderIds = Array.from(new Set(allInteractions.map(i => i.finder_user_id).filter(Boolean)));
      const ownerIds = Array.from(new Set(allInteractions.map(i => i.owner_user_id).filter(Boolean)));
      const allUserIds = Array.from(new Set([...finderIds, ...ownerIds]));
      const missingReportIds = Array.from(new Set(allInteractions.map(i => i.missing_report_id).filter(Boolean)));

      if (allUserIds.length > 0) {
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id,first_name,last_name,role')
          .in('user_id', allUserIds);
        
        if (profilesError) {
          console.error('Error fetching user profiles:', profilesError);
        }
        
        const profileMap = {};
        (profiles || []).forEach(p => {
          profileMap[p.user_id] = p;
        });
        setUserProfiles(profileMap);
      }

      if (missingReportIds.length > 0) {
        const { data: reports, error: reportsError } = await supabase
          .from('missing_reports')
          .select('id,name,category,status')
          .in('id', missingReportIds);
        
        if (reportsError) {
          console.error('Error fetching missing reports:', reportsError);
        }
        
        const reportMap = {};
        (reports || []).forEach(r => {
          reportMap[r.id] = r;
        });
        setMissingReports(reportMap);
      }

    } catch (err) {
      console.error('Error fetching interactions:', err);
      setError('Failed to load interactions');
      setInteractions([]);
    }
    
    setLoading(false);
  }

  function getUserName(user_id) {
    const profile = userProfiles[user_id];
    if (!profile) return 'Unknown User';
    return `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
  }

  function getMissingReportName(report_id) {
    const report = missingReports[report_id];
    if (!report) return 'Unknown Report';
    return `${report.name} (${report.category})`;
  }

  async function handleUpdateStatus(interactionId, newStatus) {
    try {
      // TEMPORARY WORKAROUND: First try to add missing column to missing_reports table
      try {
        const { error: alterError } = await supabase.rpc('add_updated_at_column');
        
        if (alterError) {
          console.log('Could not add column via RPC, trying manual workaround...');
        }
      } catch (alterErr) {
        console.log('Column addition failed, proceeding with manual workaround...');
      }
      
      // Get the related missing report ID to potentially update it manually
      const { data: foundReport } = await supabase
        .from('found_reports')
        .select('missing_report_id')
        .eq('id', interactionId)
        .single();
      
      if (foundReport?.missing_report_id) {
        // Try to add updated_at column to missing_reports if it doesn't exist
        try {
          await supabase
            .from('missing_reports')
            .update({ updated_at: new Date().toISOString() })
            .eq('id', foundReport.missing_report_id);
        } catch (missingUpdateError) {
          console.log('Missing reports update failed as expected, proceeding...');
        }
      }
      
      // Now try the main update
      const { data, error } = await supabase
        .from('found_reports')
        .update({ status: newStatus })
        .eq('id', interactionId)
        .select();

      if (error) {
        console.error('Update failed:', error);
        
        // Final workaround: Show manual instructions
        alert(`Database Configuration Issue Detected

The update failed because the trigger 'trigger_update_missing_report_on_found_confirmed' 
is trying to update a missing column.

IMMEDIATE FIX NEEDED:
Run this SQL command in your database:

ALTER TABLE missing_reports ADD COLUMN updated_at TIMESTAMPTZ DEFAULT NOW();

Or temporarily disable the trigger:
ALTER TABLE found_reports DISABLE TRIGGER trigger_update_missing_report_on_found_confirmed;

Then try the update again.

Technical Error: ${error.message}`);
        return;
      }

      alert('Status updated successfully!');
      
      // Refresh the interface
      fetchInteractions();
      
    } catch (err) {
      console.error('Update failed completely:', err);
      alert(`Update failed: ${err.message}

This is a database schema issue. Please contact your database administrator to add the missing 'updated_at' column to the 'missing_reports' table.`);
    }
  }

  async function handleBulkAction(action) {
    if (selectedInteractions.length === 0) {
      alert('Please select at least one interaction');
      return;
    }

    try {
      // Simple status update first to test
      const { error } = await supabase
        .from('found_reports')
        .update({ status: action })
        .in('id', selectedInteractions);

      if (error) throw error;

      setSelectedInteractions([]);
      fetchInteractions();
    } catch (err) {
      console.error('Error with bulk action:', err);
      alert('Failed to perform bulk action');
    }
  }

  const handleCheckboxChange = (id) => {
    setSelectedInteractions(prev => {
      if (prev.includes(id)) {
        return prev.filter(interactionId => interactionId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAllClick = (e) => {
    if (e.target.checked) {
      setSelectedInteractions(filteredInteractions.map(interaction => interaction.id));
    } else {
      setSelectedInteractions([]);
    }
  };

  // Filtered interactions
  const filteredInteractions = interactions.filter(interaction => {
    const finderName = getUserName(interaction.finder_user_id);
    const ownerName = getUserName(interaction.owner_user_id);
    const matchesSearch = search.trim() === '' ||
      finderName.toLowerCase().includes(search.toLowerCase()) ||
      ownerName.toLowerCase().includes(search.toLowerCase()) ||
      getMissingReportName(interaction.missing_report_id).toLowerCase().includes(search.toLowerCase()) ||
      (interaction.finder_notes && interaction.finder_notes.toLowerCase().includes(search.toLowerCase())) ||
      (interaction.finder_contact && interaction.finder_contact.toLowerCase().includes(search.toLowerCase())) ||
      (interaction.found_location && interaction.found_location.toLowerCase().includes(search.toLowerCase())) ||
      (interaction.owner_response && interaction.owner_response.toLowerCase().includes(search.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || (interaction.status || 'pending') === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Debug logging for filtering
  console.log(`🔍 Filter results: ${interactions.length} total → ${filteredInteractions.length} after filtering`);
  console.log(`📝 Search: "${search}", Status: "${statusFilter}"`);
  if (filteredInteractions.length !== interactions.length) {
    console.log(`❌ ${interactions.length - filteredInteractions.length} reports filtered out`);
  }

  const totalPages = Math.ceil(filteredInteractions.length / itemsPerPage);
  const paginatedInteractions = filteredInteractions.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  
  // Debug pagination
  console.log(`📄 Pagination: Page ${page}/${totalPages}, showing ${paginatedInteractions.length} of ${filteredInteractions.length} items`);

  function openDetailModal(interaction) {
    setSelectedInteraction(interaction);
    setShowDetailModal(true);
  }

  if (loading) return <Preloader text="Loading Interactions..." />;

  // Check if user has admin permissions
  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'super admin')) {
    return (
      <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
        <Sidebar />
        <div style={{ flex: 1, padding: 32, textAlign: 'center' }}>
          <div style={{ color: '#d32f2f', fontSize: 18, marginTop: 100 }}>
            Access denied. This feature is only available to administrators.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>

        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className="title-text">Found Reports</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />
        
        <div style={{ fontSize: 16, color: '#666', marginBottom: 24 }}>
          Monitor user reports of found missing persons and pets
        </div>

        {error && (
          <div style={{ background: '#ffebee', color: '#c62828', padding: 16, borderRadius: 8, marginBottom: 24 }}>
            {error}
          </div>
        )}

        {/* Filters and Search */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '2', minWidth: 200, maxWidth: 400 }}>
            <input
              type="text"
              placeholder="Search found reports..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 40px 12px 12px',
                borderRadius: 8,
                border: '1px solid #ccc',
                boxSizing: 'border-box'
              }}
            />
            <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: '#888', pointerEvents: 'none' }}>
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
            </span>
          </div>
          
          <select 
            value={statusFilter} 
            onChange={e => setStatusFilter(e.target.value)} 
            style={{ flex: 1, minWidth: 120, padding: 12, borderRadius: 8, border: '1px solid #ccc' }}
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
          </select>

          {/* Clear Filters Button */}
          {(search || statusFilter !== 'all') && (
            <button
              onClick={() => {
                setSearch('');
                setStatusFilter('all');
                setPage(1);
              }}
              style={{
                background: '#f44336',
                color: '#fff',
                border: 'none',
                padding: '12px 16px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 'bold'
              }}
              title="Clear all filters"
            >
              Clear Filters
            </button>
          )}

          {selectedInteractions.length > 0 && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button 
                onClick={() => handleBulkAction('confirmed')}
                style={{ 
                  background: '#28a745', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: 8, 
                  padding: '8px 16px', 
                  cursor: 'pointer' 
                }}
              >
                <FaCheck /> Confirm
              </button>
            </div>
          )}
        </div>

        {/* Interactions Table */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 0, boxShadow: '0 2px 8px #0001', minHeight: 200, overflowX: 'auto' }}>
          {error ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#d32f2f' }}>{error}</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: 12 }}>
                    <input 
                      type="checkbox" 
                      onChange={handleSelectAllClick}
                      checked={selectedInteractions.length === filteredInteractions.length && filteredInteractions.length > 0}
                    />
                  </th>
                  <th style={{ padding: 12 }}>Finder</th>
                  <th style={{ padding: 12 }}>Owner</th>
                  <th style={{ padding: 12 }}>Missing Report</th>
                  <th style={{ padding: 12 }}>Found Location</th>
                  <th style={{ padding: 12 }}>Notes</th>
                  <th style={{ padding: 12 }}>Owner Response</th>
                  <th style={{ padding: 12 }}>Status</th>
                  <th style={{ padding: 12 }}>Date</th>
                  <th style={{ padding: 12 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedInteractions.length > 0 ? paginatedInteractions.map(interaction => (
                  <tr key={`found-report-${interaction.id}`} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: 12 }}>
                      <input
                        type="checkbox"
                        checked={selectedInteractions.includes(interaction.id)}
                        onChange={() => handleCheckboxChange(interaction.id)}
                      />
                    </td>
                    <td style={{ padding: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {getUserName(interaction.finder_user_id)}
                    </td>
                    <td style={{ padding: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {getUserName(interaction.owner_user_id)}
                    </td>
                    <td style={{ padding: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {getMissingReportName(interaction.missing_report_id)}
                    </td>
                    <td style={{ padding: 12, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {interaction.found_location || 'Not specified'}
                    </td>
                    <td style={{ padding: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {interaction.finder_notes || 'No notes'}
                    </td>
                    <td style={{ padding: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {interaction.owner_response || 'No response yet'}
                    </td>
                    <td style={{ padding: 12 }}>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontWeight: 600,
                          fontSize: 14,
                          background: 
                            (interaction.status || 'pending') === 'pending' ? '#fff3cd' :
                            (interaction.status || 'pending') === 'confirmed' ? '#d4edda' :
                            '#f8d7da',
                          color:
                            (interaction.status || 'pending') === 'pending' ? '#856404' :
                            (interaction.status || 'pending') === 'confirmed' ? '#155724' :
                            '#721c24'
                        }}
                      >
                        {(interaction.status || 'pending').charAt(0).toUpperCase() + (interaction.status || 'pending').slice(1)}
                      </span>
                    </td>
                    <td style={{ padding: 12 }}>
                      {new Date(interaction.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => openDetailModal(interaction)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#08b1c7',
                            cursor: 'pointer',
                            fontSize: 18
                          }}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <select
                          value={interaction.status || 'pending'}
                          onChange={e => handleUpdateStatus(interaction.id, e.target.value)}
                          style={{ 
                            padding: '4px 8px', 
                            borderRadius: 4, 
                            border: '1px solid #ccc',
                            fontSize: 12
                          }}
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                      No found reports found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
          <button 
            onClick={() => setPage(p => Math.max(1, p - 1))} 
            disabled={page === 1} 
            style={{ 
              background: '#eee', 
              border: 'none', 
              borderRadius: 4, 
              padding: '4px 12px', 
              cursor: page === 1 ? 'not-allowed' : 'pointer' 
            }}
          >
            Previous
          </button>
          <div style={{ display: 'flex', gap: 4 }}>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                onClick={() => setPage(i + 1)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  cursor: 'pointer',
                  background: page === i + 1 ? '#08b1c7' : '#fff',
                  color: page === i + 1 ? '#fff' : '#333',
                  fontWeight: page === i + 1 ? 'bold' : 'normal'
                }}
              >
                {i + 1}
              </button>
            ))}
          </div>
          <button 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))} 
            disabled={page === totalPages} 
            style={{ 
              background: '#eee', 
              border: 'none', 
              borderRadius: 4, 
              padding: '4px 12px', 
              cursor: page === totalPages ? 'not-allowed' : 'pointer' 
            }}
          >
            Next
          </button>
        </div>

        {/* Detail Modal */}
        {showDetailModal && selectedInteraction && (
          <div style={{
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            background: '#0008', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              padding: 32,
              maxWidth: 600,
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 4px 32px rgba(8, 177, 199, 0.18)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h2 style={{ margin: 0, fontSize: 24 }}>
                  Found Report Details
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  style={{ background: 'none', border: 'none', fontSize: 28, cursor: 'pointer', color: '#888' }}
                >
                  ×
                </button>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Finder:</label>
                  <p style={{ margin: '4px 0 0 0' }}>{getUserName(selectedInteraction.finder_user_id)}</p>
                </div>
                
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Original Owner:</label>
                  <p style={{ margin: '4px 0 0 0' }}>{getUserName(selectedInteraction.owner_user_id)}</p>
                </div>
                
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Missing Report:</label>
                  <p style={{ margin: '4px 0 0 0' }}>{getMissingReportName(selectedInteraction.missing_report_id)}</p>
                </div>
                
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Found Location:</label>
                  <p style={{ margin: '4px 0 0 0', lineHeight: 1.5 }}>
                    {selectedInteraction.found_location || 'Not specified'}
                  </p>
                </div>
                
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Finder's Notes:</label>
                  <p style={{ margin: '4px 0 0 0', lineHeight: 1.5 }}>
                    {selectedInteraction.finder_notes || 'No notes provided'}
                  </p>
                </div>
                
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Finder's Contact:</label>
                  <p style={{ margin: '4px 0 0 0' }}>
                    {selectedInteraction.finder_contact || 'Not provided'}
                  </p>
                </div>
                
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Owner's Response:</label>
                  <p style={{ margin: '4px 0 0 0', lineHeight: 1.5 }}>
                    {selectedInteraction.owner_response || 'No response yet'}
                  </p>
                </div>
                
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Status:</label>
                  <p style={{ margin: '4px 0 0 0' }}>
                    {(selectedInteraction.status || 'pending').charAt(0).toUpperCase() + (selectedInteraction.status || 'pending').slice(1)}
                  </p>
                </div>
                
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Date Submitted:</label>
                  <p style={{ margin: '4px 0 0 0' }}>
                    {new Date(selectedInteraction.created_at).toLocaleString()}
                  </p>
                </div>
                
                {selectedInteraction.confirmed_at && (
                  <div>
                    <label style={{ fontWeight: 'bold', color: '#666' }}>Date Confirmed:</label>
                    <p style={{ margin: '4px 0 0 0' }}>
                      {new Date(selectedInteraction.confirmed_at).toLocaleString()}
                    </p>
                  </div>
                )}
                
                {selectedInteraction.updated_at && selectedInteraction.updated_at !== selectedInteraction.created_at && (
                  <div>
                    <label style={{ fontWeight: 'bold', color: '#666' }}>Last Updated:</label>
                    <p style={{ margin: '4px 0 0 0' }}>
                      {new Date(selectedInteraction.updated_at).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
              
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 24 }}>
                <button
                  onClick={() => {
                    handleUpdateStatus(selectedInteraction.id, 'confirmed');
                    setShowDetailModal(false);
                  }}
                  style={{
                    background: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 24px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  Confirm Found
                </button>
                <button
                  onClick={() => setShowDetailModal(false)}
                  style={{
                    background: '#eee',
                    color: '#333',
                    border: 'none',
                    borderRadius: 8,
                    padding: '12px 24px',
                    cursor: 'pointer'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MissingReportInteractions;